import React, { useEffect, useState } from 'react';
import { always, isEmpty } from 'ramda';
import { AnimatePresence } from 'framer-motion';
import { useMountedState } from 'react-use';
import { useInView } from 'react-intersection-observer';
import { parse } from 'query-string';
import { Element } from 'react-scroll';

import { useFetchArtworks, useSetCurrentPage } from '@hooks/useArtworks';
import { ArtworkList } from '@components/artworkList';
import { renderWhenTrue } from '@utils/rendering';
import { InfiniteLoader } from '@components/PLP/infiniteLoader';
import reportError from '@utils/reportError';
import { HIDDEN_BOTTOM, PLP_VARIANT } from '@modules/plp';
import { artworksDomain } from '@modules/artworks/artworks.selectors';
import { ArtworksPagination } from '@components/PLP/artworksPagination';
import { NoResults } from '@components/noResults';
import { isBrowser } from '@utils/helpers';
import { ACTION_PREFIX } from '@modules/plp/plp.constants';
import { GlobalState } from '@modules/createStore';
import { ArtworksState } from '@definitions/artworks.types';
import { getCurrentPLP } from '@modules/plp/plp.helpers';

import { Container, ListContainer, HiddenBottom, imageStyles } from './searchView.styles';

export const SearchView = () => {
  const [loading, setLoading] = useState(false);
  const [bottomRef, inView] = useInView();
  const currentPLP = getCurrentPLP();
  const { artworks, meta, fetchArtworks, loading: artworksLoading } = useFetchArtworks(PLP_VARIANT.ARTWORKS);
  const isMounted = useMountedState();
  const hasArtworks = !isEmpty(artworks);

  const { currentPage } = useSetCurrentPage(currentPLP);

  const getQuerySearch = () => {
    if (isBrowser()) {
      const query = parse(location.search);
      return query.search || '';
    }

    return '';
  };
  const fetchNextArtworks = async () => {
    try {
      setLoading(true);
      const search = getQuerySearch();

      if (search.length) {
        await fetchArtworks({ page: meta.page + 1, search, infiniteLoader: true });
      }
    } catch (e) {
      reportError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (inView && !loading && meta.hasNext) {
      fetchNextArtworks().finally(() => ({}));
    }
  }, [inView]);

  useEffect(() => {
    if (currentPage === meta.page && currentPage !== 1 && meta.hasNext) {
      fetchNextArtworks().finally(() => ({}));
    }
  }, [currentPage]);

  const renderArtworks = renderWhenTrue(() => (
    <ListContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}
      exit={{ opacity: 0, y: 20, transition: { duration: 0.6 } }}
    >
      <ArtworkList artworks={artworks} imageStyles={imageStyles} renderHiddenContainer />
    </ListContainer>
  ));

  const renderHiddenBottom = renderWhenTrue(
    always(
      <Element name={HIDDEN_BOTTOM}>
        <HiddenBottom ref={bottomRef} />
      </Element>
    )
  );

  return (
    <Container>
      <AnimatePresence>{renderArtworks(hasArtworks && isMounted())}</AnimatePresence>
      <NoResults isVisible={!hasArtworks && !artworksLoading && !!getQuerySearch()} />
      <InfiniteLoader isVisible={loading} />
      {renderHiddenBottom(isMounted() && !!artworks.length)}
    </Container>
  );
};
